import Vue from 'vue'
import { cloneDeep } from 'lodash'
import UUID from 'uuid/v4'
import { Dashboard } from '@/store/schema'
import { normalize } from 'normalizr'
import { stripOutDashboardProperty, stripOutDashboardNulls } from '@/utils/helpers'
import km from '@/utils/pm2Io'

// Mutations
export const SET_CURRENT_DASHBOARD = 'SET_CURRENT_DASHBOARD'
export const REMOVE_DASHBOARD_ITEM = 'REMOVE_DASHBOARD_ITEM'
export const SET_IS_COMPONENT_PICKER_OPEN = 'SET_IS_COMPONENT_PICKER_OPEN'
export const SET_EDIT_DIALOG_INTENT = 'SET_EDIT_DIALOG_INTENT'
export const SET_COMPONENT_BEING_EDITED = 'SET_COMPONENT_BEING_EDITED'
const SET_DASHBOARDS = 'SET_DASHBOARDS'
const ADD_DASHBOARD = 'ADD_DASHBOARD'
export const UPDATE_DASHBOARD = 'UPDATE_DASHBOARD'
export const SET_COMPONENT_BEING_EDITED_PARAM = 'SET_COMPONENT_BEING_EDITED_PARAM'
// DASHBOARD REFACTOR MUTATIONS
export const APPLY_CHANGES_TO_COMPONENT_BEING_EDITED = 'APPLY_CHANGES_TO_COMPONENT_BEING_EDITED'
export const ADD_COMPONENT_TO_DASHBOARD = 'ADD_COMPONENT_TO_DASHBOARD'
export const UPDATE_CURRENT_DASHBOARD = 'UPDATE_CURRENT_DASHBOARD'
export const SET_HOVERED_COMPONENT = 'SET_HOVERED_COMPONENT'
export const RESET_CURRENT_DASHBOARD = 'RESET_CURRENT_DASHBOARD'

// Actions
export const FETCH_ALL_DASHBOARDS = 'FETCH_ALL_DASHBOARDS'
export const CREATE_DASHBOARD = 'CREATE_DASHBOARD'
export const DELETE_DASHBOARD = 'DELETE_DASHBOARD'

function migrateDashboard(dashboard) {
  for (const child of dashboard.children) {
    if (child.component === 'continuous-metric-2') {
      // Migrate continuous-migrate-2 to continuous-metric components
      child.params.isRealtime = true
      child.params.aggregationType = 'AVERAGE'
      child.component = 'continuous-metric'
    } else if (child.component === 'continuous-metric-3') {
      // continuous-metric-3 has been renamed to continuous-metric
      child.component = 'continuous-metric'
    }
  }
}

const initialState = {
  dashboards: {},
  hoveredComponent: null,
  currentDashboard: null,
  editDialogIntent: null,
  isComponentPickerOpen: false,
  componentBeingEdited: {
    type: 'component',
    uuid: null,
    params: {},
    component: null,
  },
}

const mutations = {
  [SET_CURRENT_DASHBOARD](state, { name }) {
    const stateDashboard = state.dashboards[name]
    // We need to give each child in the dashboard a uid that will be used as a ref for its DOM element
    function generateUid(child) {
      if (!child.uuid) child.uuid = UUID()
      if (child.component === 'metric-category') {
        child.children = child.children.map(generateUid)
      }
      return child
    }
    if (stateDashboard.children.length !== 0) {
      stateDashboard.children = stateDashboard.children.map(generateUid)
    }
    state.currentDashboard = cloneDeep(stateDashboard)
  },
  [REMOVE_DASHBOARD_ITEM](state, { uuid }) {
    function isNotUid(child, uuid) {
      return child.uuid !== uuid
    }
    function filterByCurrentUid(child) {
      if (child.component === 'metric-category') {
        child.children = child.children.filter(filterByCurrentUid)
      }
      return isNotUid(child, uuid)
    }
    const newChildren = state.currentDashboard.children.filter(filterByCurrentUid)
    Vue.set(state.currentDashboard, 'children', newChildren)
  },
  [SET_COMPONENT_BEING_EDITED_PARAM](state, { key, param }) {
    const params = state.componentBeingEdited.params
    const oldParam = params[key]
    let newParam = param
    if (typeof oldParam === 'object' && !Array.isArray(oldParam)) {
      newParam = Object.assign({}, params[key], param)
    }
    Vue.set(params, key, newParam)
  },
  [APPLY_CHANGES_TO_COMPONENT_BEING_EDITED](state) {
    const componentBeingEdited = state.componentBeingEdited
    // let componentObj = getters.getDashboardItemReferenceByUuid(state)({
    //   uuid: componentBeingEdited.uuid
    // })
    const initialComp = state.currentDashboard.children.find(
      comp => comp.uuid === componentBeingEdited.uuid,
    )
    initialComp.params = cloneDeep(componentBeingEdited.params)
  },
  [SET_IS_COMPONENT_PICKER_OPEN](state, { isComponentPickerOpen }) {
    state.isComponentPickerOpen = isComponentPickerOpen
  },
  [SET_EDIT_DIALOG_INTENT](state, { editDialogIntent }) {
    state.editDialogIntent = editDialogIntent
  },
  [SET_COMPONENT_BEING_EDITED](state, { componentBeingEdited }) {
    state.componentBeingEdited = componentBeingEdited
  },
  [ADD_COMPONENT_TO_DASHBOARD](state, { component }) {
    const currentDashboard = state.currentDashboard

    // Now we need to find a place to put the new component
    let idealX = 0
    if (currentDashboard.children.length !== 0) {
      idealX = currentDashboard.children
        .map(comp => comp.params.pos.x + comp.params.pos.w)
        .reduce((a, b) => Math.max(a, b))
    }
    const maxX = 20 - component.params.pos.defaultW
    component.params.pos.x = idealX > maxX ? 0 : idealX
    component.params.pos.y = 0

    component.params.pos.w = component.params.pos.defaultW
    component.params.pos.h = component.params.pos.defaultH

    currentDashboard.children.push(component)
    state.currentDashboard = currentDashboard
  },
  [SET_DASHBOARDS](state, { dashboards }) {
    for (const id in dashboards) migrateDashboard(dashboards[id])
    state.dashboards = dashboards
  },
  [ADD_DASHBOARD](state, { dashboard }) {
    Vue.set(state.dashboards, dashboard._id, dashboard)
  },
  [UPDATE_DASHBOARD](state, { dashboard }) {
    Vue.set(state.dashboards, dashboard._id, dashboard)
  },
  [UPDATE_CURRENT_DASHBOARD](state, { dashboard }) {
    state.currentDashboard = dashboard
  },
  [SET_HOVERED_COMPONENT](state, { hoveredComponent }) {
    state.hoveredComponent = hoveredComponent
  },
  [RESET_CURRENT_DASHBOARD](state, { dashboardId }) {
    state.currentDashboard = cloneDeep(state.dashboards[dashboardId])
  },
}

const getters = {
  dashboard: state => ({ dashboardId }) => {
    if (state.dashboards[dashboardId]) return state.dashboards[dashboardId]
    else return null
  },
  dashboards: state => () => {
    let dashboards = Object.values(state.dashboards)
    dashboards = dashboards.sort((dash1, dash2) => {
      const date1 = new Date(dash1.created_at)
      const date2 = new Date(dash2.created_at)
      if (date1 > date2) return 1
      if (date1 < date2) return -1
      return 0
    })
    return dashboards
  },
  currentDashboard: state => () => {
    return state.currentDashboard
  },
  getDashboardItemByUuid: state => ({ uuid }) => {
    const currentDashboard = state.currentDashboard
    let res = currentDashboard.children.find(el => el.uuid === uuid)
    // Is the result is not in the first children, find it a level deeper
    if (!res) {
      res = currentDashboard.children
        .filter(el => el.children)
        .map(el => el.children)
        .reduce((a, b) => a.concat(b), [])
        .find(el => el.uuid === uuid)
    }
    // Here we return a cloned value because we want the item representation, not its actual reference
    res = cloneDeep(res)

    return res
  },
  getDashboardItemReferenceByUuid: state => ({ uuid }) => {
    const currentDashboard = state.currentDashboard
    let res = currentDashboard.children.find(el => el.uuid === uuid)
    // Is the result is not in the first children, find it a level deeper
    if (!res) {
      res = currentDashboard.children
        .filter(el => el.children)
        .map(el => el.children)
        .reduce((a, b) => a.concat(b), [])
        .find(el => el.uuid === uuid)
    }
    // Here we return a direct reference for direct modification of the item
    return res
  },
  componentBeingEdited: state => () => {
    return state.componentBeingEdited
  },
  isComponentPickerOpen: state => () => {
    return state.isComponentPickerOpen
  },
  editDialogIntent: state => () => {
    return state.editDialogIntent
  },
  hoveredComponent: state => () => {
    return state.hoveredComponent
  },
}

const actions = {
  [FETCH_ALL_DASHBOARDS]({ commit, getters }, { bucketId = getters.currentBucket.bucket.id }) {
    return km.dashboard.retrieveAll(bucketId, {}).then(res => {
      const dashboards = res.data
      if (dashboards.length === 0) return
      const normDashboards = normalize(dashboards, [Dashboard]).entities.dashboards
      commit(SET_DASHBOARDS, { dashboards: normDashboards })
    })
  },
  [CREATE_DASHBOARD](
    { commit, getters },
    { bucketId = getters.currentBucket.bucket.id, dashboard },
  ) {
    return km.dashboard.create(bucketId, dashboard).then(res => {
      commit(ADD_DASHBOARD, { dashboard: res.data })
      return res.data
    })
  },
  [DELETE_DASHBOARD](
    { state, getters },
    { bucketId = getters.currentBucket.bucket.id, dashboardId },
  ) {
    return km.dashboard.remove(bucketId, dashboardId).then(res => {
      Vue.delete(state.dashboards, dashboardId)
      return res
    })
  },
  // TODO: remove useless two levels stripout since we now rely on only one level of components
  [UPDATE_DASHBOARD](
    { commit, getters },
    { bucketId = getters.currentBucket.bucket.id, dashboard },
  ) {
    // First we need to make we do not save any uuid
    dashboard = stripOutDashboardNulls(dashboard)
    dashboard = stripOutDashboardProperty(dashboard, 'uuid')
    // First we need to make we do not save any uuid
    dashboard = stripOutDashboardProperty(dashboard, '_id')
    return km.dashboard.update(bucketId, dashboard._id, dashboard).then(res => {
      commit(UPDATE_DASHBOARD, { dashboard: res.data })
      return res
    })
  },
}

export default {
  state: cloneDeep(initialState),
  actions,
  getters,
  mutations,
}
